import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { I_CommentScene } from 'containers/Home/types';
import { Popover } from 'antd';
import dynamic from 'next/dynamic';
import { useCollaborationMode } from '@containers/Home/global/TourStats';
import LoadingContainer from '@components/LoadingContainer';
import { CommentSceneIconWrapper } from './styled';

const PopoverCommentScene = dynamic(() => import('./CommentScenePopover'));
const SvgContainer = dynamic(() => import('@components/SvgContainer'));

interface Props {
  commentScene: I_CommentScene;
  onCreateCommentScene: (data, krpanoInstance) => void;
  onUpdateCommentScene: (data, krpanoInstance) => void;
  onDeleteCommentScene: (data, krpanoInstace, isClosePopoverOnKrpano) => void;
}

export type TypeRefCommentSceneIcon = {
  onClose: () => void;
};

const CommentSceneIcon = forwardRef(
  (
    {
      commentScene,
      onCreateCommentScene,
      onUpdateCommentScene,
      onDeleteCommentScene,
    }: Props,
    ref: Ref<TypeRefCommentSceneIcon>,
  ) => {
    const [isShow, setIsShow] = useState(false);
    const [krpanoInstance] = useCollaborationMode('krpanoRef');
    const [commentSceneSelected, setCommentSelected] = useCollaborationMode(
      'commentSceneSelected',
    );
    const [loading] = useCollaborationMode('loading');

    useEffect(
      () => () => {
        setIsShow(false);
      },
      [],
    );

    useEffect(() => {
      if (commentSceneSelected) {
        setIsShow(commentSceneSelected.id === commentScene.id);
      } else setIsShow(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commentSceneSelected]);

    const onClosePopover = useCallback(() => {
      setCommentSelected(null);
    }, [setCommentSelected]);

    const _onCreateCommentScene = useCallback(
      data => onCreateCommentScene(data, krpanoInstance),
      [krpanoInstance, onCreateCommentScene],
    );

    const _onUpdateCommentScene = useCallback(
      data => onUpdateCommentScene(data, krpanoInstance),
      [krpanoInstance, onUpdateCommentScene],
    );

    const _onDeleteCommentScene = useCallback(
      (data, isClosePopover = false) =>
        onDeleteCommentScene(data, krpanoInstance, isClosePopover),
      [krpanoInstance, onDeleteCommentScene],
    );

    useImperativeHandle(ref, () => ({
      onClose() {
        setIsShow(false);
        onClosePopover();
      },
    }));

    return (
      <Popover
        visible={isShow}
        content={
          <LoadingContainer loading={loading}>
            <PopoverCommentScene
              onClose={onClosePopover}
              commentSceneSelected={commentScene}
              onCreateCommentScene={_onCreateCommentScene}
              onUpdateCommentScene={_onUpdateCommentScene}
              onDeleteCommentScene={_onDeleteCommentScene}
            />
          </LoadingContainer>
        }
        placement="bottomLeft"
        destroyTooltipOnHide
        trigger="click"
        zIndex={99}
      >
        <CommentSceneIconWrapper
          // onClick={onClick}
          className={commentScene.isResolved ? 'resolved' : ''}
        >
          <SvgContainer
            src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/core/comment.svg`}
            name="4"
          />
          <span className="--icon">{commentScene?.index || 0}</span>
        </CommentSceneIconWrapper>
      </Popover>
    );
  },
);

export default CommentSceneIcon;
